import { compareTwoStrings } from "string-similarity";

// Thresholds agreed for the mic check: below MIN_THRESHOLD the candidate must
// retry, between MIN_THRESHOLD and PASS_THRESHOLD they're warned but may
// proceed, at/above PASS_THRESHOLD they proceed without a warning.
export const PASS_THRESHOLD = 0.8;
export const MIN_THRESHOLD = 0.6;

// How similar two individual words need to be (Dice coefficient) to count as
// the "same word" once an exact match hasn't been found - tolerates minor
// mis-transcriptions (e.g. "clear" vs "clean") without being so loose that
// unrelated words match each other.
const WORD_FUZZY_THRESHOLD = 0.8;

/**
 * Lowercase, strip punctuation (keeping apostrophes for contractions) and
 * collapse whitespace, so "Clear, today!" and "clear today" compare equal.
 */
export function normalizePhrase(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(text) {
  const normalized = normalizePhrase(text);
  return normalized.length ? normalized.split(" ") : [];
}

/**
 * Word-level F1 between the expected phrase and what Deepgram actually heard.
 *
 * Deliberately not a whole-string similarity (e.g. raw compareTwoStrings on
 * the full sentence): that metric is sensitive to length and word order, so
 * one filler word can tank an otherwise-clear reading while a noisy
 * transcript of the wrong length can still score well by coincidence.
 * Word-level recall/precision instead penalizes exactly the two failure
 * modes a mic check cares about - words the microphone failed to pick up
 * (recall), and words the room's noise fabricated (precision).
 *
 * @returns {{ score: number, matchedWords: boolean[], missingWords: string[] }}
 *   matchedWords is indexed against the expected phrase's word order, so a
 *   caller can zip it with the original phrase's words to highlight matches.
 */
export function matchPhrase(expected, actual) {
  const expectedWords = tokenize(expected);
  const actualWords = tokenize(actual);
  const matchedWords = new Array(expectedWords.length).fill(false);

  if (expectedWords.length === 0) {
    return { score: 0, matchedWords, missingWords: [] };
  }

  const usedActualIndices = new Set();
  let matched = 0;

  // Pass 1: exact word matches.
  expectedWords.forEach((expectedWord, i) => {
    const exactIndex = actualWords.findIndex(
      (actualWord, j) => !usedActualIndices.has(j) && actualWord === expectedWord
    );
    if (exactIndex !== -1) {
      usedActualIndices.add(exactIndex);
      matchedWords[i] = true;
      matched += 1;
    }
  });

  // Pass 2: fuzzy matches for whatever's left, to tolerate minor
  // mis-transcriptions without conflating them with truly missing words.
  expectedWords.forEach((expectedWord, i) => {
    if (matchedWords[i]) return;
    let bestIndex = -1;
    let bestScore = 0;
    actualWords.forEach((actualWord, j) => {
      if (usedActualIndices.has(j)) return;
      const score = compareTwoStrings(expectedWord, actualWord);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = j;
      }
    });
    if (bestIndex !== -1 && bestScore >= WORD_FUZZY_THRESHOLD) {
      usedActualIndices.add(bestIndex);
      matchedWords[i] = true;
      matched += 1;
    }
  });

  const recall = matched / expectedWords.length;
  const precision = actualWords.length ? matched / actualWords.length : 0;
  const score = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);
  const missingWords = expectedWords.filter((_, i) => !matchedWords[i]);

  return { score, matchedWords, missingWords };
}
